import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const NAV_ITEMS = [
  { to: '/', label: '首页', end: true },
  { to: '/category', label: '电影分类', end: false },
  { to: '/news', label: '资讯动态', end: false },
  { to: '/about', label: '关于我们', end: false },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/category?q=${encodeURIComponent(q)}` : '/category');
    setMenuOpen(false);
  };

  const focusSearch = () => {
    setSearchOpen(true);
    window.setTimeout(() => searchRef.current?.focus(), 50);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
        scrolled
          ? 'border-background-200 bg-background-50/95 backdrop-blur-md'
          : 'border-transparent bg-background-50'
      }`}
    >
      <div className="flex h-16 items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-movie-2-line text-lg"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-base font-bold tracking-tight text-foreground-950 whitespace-nowrap">
              北条麻妃电影
            </span>
            <span className="mt-0.5 hidden text-[11px] text-foreground-500 sm:block whitespace-nowrap">
              专业影视作品资讯平台
            </span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 lg:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `rounded-md px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden md:block">
            <div
              className={`flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 transition-all duration-300 ${
                searchOpen ? 'w-64' : 'w-48'
              } focus-within:border-primary-400 focus-within:bg-background-50`}
            >
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                ref={searchRef}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setSearchOpen(false)}
                type="text"
                placeholder="搜索电影标题"
                aria-label="搜索电影标题"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword('')}
                  className="text-foreground-400 hover:text-foreground-700"
                  aria-label="清空搜索"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
          </form>

          <button
            type="button"
            onClick={focusSearch}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-300 text-foreground-700 md:hidden"
            aria-label="搜索"
          >
            <i className="ri-search-line"></i>
          </button>

          <Link
            to="/category"
            className="hidden items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 lg:flex whitespace-nowrap"
          >
            <i className="ri-apps-2-line text-base"></i>
            全部作品
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? 'ri-close-line text-lg' : 'ri-menu-3-line text-lg'}></i>
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 md:hidden">
          <form onSubmit={submitSearch}>
            <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-2 focus-within:border-primary-400">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                ref={searchRef}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索电影标题"
                aria-label="搜索电影标题"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button type="submit" className="text-primary-600 text-sm font-medium">
                搜索
              </button>
            </div>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          <nav className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `rounded-md px-4 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}